import { forwardRef, useState, useCallback, useEffect, useRef } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "./Input";
import type { InputProps } from "./types";

export interface PasswordInputProps extends Omit<InputProps, "type"> {
  autoHideMs?: number;
  showLabel?: string;
  hideLabel?: string;
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput(
    { autoHideMs = 30_000, showLabel, hideLabel, className = "", ...props },
    ref,
  ) {
    const [visible, setVisible] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearTimer = useCallback(() => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }, []);

    const toggle = useCallback(() => {
      clearTimer();
      setVisible((prev) => {
        if (!prev && autoHideMs > 0) {
          timerRef.current = setTimeout(() => setVisible(false), autoHideMs);
        }
        return !prev;
      });
    }, [autoHideMs, clearTimer]);

    useEffect(() => clearTimer, [clearTimer]);

    return (
      <div className="relative">
        <Input
          ref={ref}
          {...props}
          type={visible ? "text" : "password"}
          className={`pr-10 ${className}`}
        />
        <button
          type="button"
          onClick={toggle}
          className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted transition-colors hover:text-primary"
          tabIndex={-1}
          aria-label={
            visible
              ? (hideLabel ?? "Hide password")
              : (showLabel ?? "Show password")
          }
        >
          {visible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    );
  },
);
